/**
 * Convocação de assembleia (0.1.88, Doc. Técnica §67).
 *
 * Gestão (`AssembleiasGestao`, dentro de Votações): o síndico escolhe tipo (AGO/AGE),
 * formato, data, horário da 1ª e 2ª convocação, local/link e descreve a ordem do dia
 * (com itens prontos). Salva como rascunho, confere o edital (MINUTA) e **envia**: aviso
 * fixado + e-mail + celular + WhatsApp, com comprovante de cada envio. Depois baixa a
 * lista de presença (com o logo do prédio), a procuração e o comprovante, e pode criar a
 * enquete prévia ou a votação on-line com os itens da pauta.
 *
 * Administradora: o síndico marca se ela PARTICIPA (secretaria e faz a ata — sai no
 * edital) e/ou se ELA ENVIA o e-mail aos moradores (o app então só publica no Portal e
 * avisa no celular/WhatsApp). "Para a administradora" baixa um PDF único e prepara o texto
 * do e-mail — o mesmo método do Relatório para a Administradora.
 *
 * Morador (`AssembleiasMorador`, no Portal > Votações): vê as convocações e baixa o
 * edital e o modelo de procuração.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, ArrowDown, ArrowUp, BarChart3, Ban, Briefcase, CheckCircle2, ClipboardList,
  Download, FileSignature, FileText, Gavel, Loader2, Pencil, Plus, Send, Trash2, Users, Vote, X,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { api, ApiError, assembleiaPdfUrl } from "../lib/api";
import { AVISO_ANEXO, BotoesEmail } from "./EmailPronto";

export interface Assembleia {
  id: number; tipo: string; formato: string; nome_tipo: string;
  primeira_em: string; segunda_em: string; local: string | null; link: string | null;
  pauta: string[]; observacoes: string | null; status: string;
  enviada_em: string | null; enviada_por: string | null; lembrete_em: string | null;
  votacao_id: number | null; enquete_id: number | null; titulo_curto: string;
  dias_antecedencia: number;
  administradora_participa: boolean; envio_moradores: string;
  envios?: { unidades: number; email_ok: number; email_falhou: number; sem_email: string[];
    celular: number; lembretes: number };
}
interface ModeloPauta { titulo: string; texto: string }

const FORMATOS: { v: string; rotulo: string }[] = [
  { v: "PRESENCIAL", rotulo: "Presencial" },
  { v: "VIRTUAL", rotulo: "On-line" },
  { v: "HIBRIDA", rotulo: "Híbrida" },
];
const STATUS: Record<string, { rotulo: string; cls: string }> = {
  RASCUNHO: { rotulo: "Rascunho", cls: "text-slate-600 bg-slate-100 border-slate-200" },
  ENVIADA: { rotulo: "Convocada", cls: "text-teal-700 bg-teal-50 border-teal-200" },
  REALIZADA: { rotulo: "Realizada", cls: "text-blue-700 bg-blue-50 border-blue-200" },
  CANCELADA: { rotulo: "Cancelada", cls: "text-rose-700 bg-rose-50 border-rose-200" },
};

const FORM0 = { tipo: "AGE", formato: "PRESENCIAL", data: "", hora1: "19:00", hora2: "19:30",
  local: "", link: "", pauta: [""], observacoes: "", adm_participa: false, adm_envia: false };
type Form = typeof FORM0;

/** "2026-10-28T19:00:00-03:00" (o backend já manda em Brasília) > "qua, 28/10/2026 às 19h". */
export function quandoBr(iso: string): string {
  const [d, t] = iso.split("T");
  const [a, m, dia] = d.split("-");
  const [h, min] = (t || "00:00").split(":");
  const sem = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"][new Date(`${d}T12:00:00`).getDay()];
  return `${sem}, ${dia}/${m}/${a} às ${Number(h)}h${min !== "00" ? min : ""}`;
}
const hora = (iso: string) => { const [h, m] = iso.split("T")[1].split(":"); return `${Number(h)}h${m !== "00" ? m : ""}`; };

function somarMinutos(hhmm: string, min: number): string {
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const t = (h * 60 + m + min) % (24 * 60);
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
}

function diasAte(data: string): number | null {
  if (!data) return null;
  const hoje = new Date(); hoje.setHours(12, 0, 0, 0);
  return Math.round((new Date(`${data}T12:00:00`).getTime() - hoje.getTime()) / 86400000);
}

const msg = (e: unknown) => (e instanceof ApiError ? e.message : "Não foi possível concluir.");

// =====================================================================
// Gestão
// =====================================================================
export function AssembleiasGestao({ condominioId, onVotacaoCriada }: {
  condominioId: number; onVotacaoCriada: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<Form | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const chave = ["assembleias", condominioId];
  const { data: lista } = useQuery<Assembleia[]>({
    queryKey: chave, queryFn: () => api.get(`/condominios/${condominioId}/assembleias`),
  });
  const { data: modelos } = useQuery<{ itens: ModeloPauta[]; antecedencia_dias: number }>({
    queryKey: ["assembleias-modelos"], queryFn: () => api.get("/assembleias/modelos-pauta"),
    staleTime: Infinity,
  });
  const antecedencia = modelos?.antecedencia_dias ?? 8;
  const { data: adm } = useQuery<{ nome: string | null }>({
    queryKey: ["assembleias-adm", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/assembleias/administradora`),
    staleTime: 5 * 60_000,
  });

  const salvar = useMutation({
    mutationFn: (f: Form) => {
      const corpo = {
        tipo: f.tipo, formato: f.formato, primeira_em: `${f.data}T${f.hora1}`,
        segunda_em: `${f.data}T${f.hora2}`, local: f.formato === "VIRTUAL" ? "" : f.local,
        link: f.formato === "PRESENCIAL" ? "" : f.link,
        pauta: f.pauta.map((p) => p.trim()).filter(Boolean), observacoes: f.observacoes,
        administradora_participa: f.adm_participa, envio_moradores: f.adm_envia ? "ADMINISTRADORA" : "APP",
      };
      return editId ? api.patch(`/assembleias/${editId}`, corpo)
        : api.post(`/condominios/${condominioId}/assembleias`, corpo);
    },
    onSuccess: () => { setForm(null); setEditId(null); setErro(null); qc.invalidateQueries({ queryKey: chave }); },
    onError: (e) => setErro(msg(e)),
  });

  function editar(a: Assembleia) {
    setEditId(a.id); setErro(null);
    setForm({
      tipo: a.tipo, formato: a.formato, data: a.primeira_em.slice(0, 10),
      hora1: a.primeira_em.slice(11, 16), hora2: a.segunda_em.slice(11, 16),
      local: a.local ?? "", link: a.link ?? "", pauta: a.pauta.length ? a.pauta : [""],
      observacoes: a.observacoes ?? "", adm_participa: a.administradora_participa,
      adm_envia: a.envio_moradores === "ADMINISTRADORA",
    });
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 mb-6">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h2 className="font-semibold text-slate-800 flex items-center gap-2">
          <Gavel size={17} className="text-teal-600" /> Assembleias
        </h2>
        {!form && (
          <button onClick={() => { setEditId(null); setErro(null); setForm({ ...FORM0, pauta: [""] }); }}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-brand-700 text-white hover:brightness-110">
            <Plus size={15} /> Convocar assembleia
          </button>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Descreva a pauta e o app gera o edital, a lista de presença e a procuração, e avisa todos os moradores.
      </p>

      {form && (
        <FormAssembleia form={form} setForm={setForm} modelos={modelos?.itens ?? []} antecedencia={antecedencia}
          nomeAdm={adm?.nome ?? null}
          editando={!!editId} erro={erro} salvando={salvar.isPending}
          onSalvar={() => salvar.mutate(form)} onFechar={() => { setForm(null); setEditId(null); }} />
      )}

      <div className="space-y-2">
        {(lista ?? []).map((a) => (
          <CartaoAssembleia key={a.id} a={a} antecedencia={antecedencia} onEditar={() => editar(a)}
            onMudou={() => qc.invalidateQueries({ queryKey: chave })} onVotacaoCriada={onVotacaoCriada} />
        ))}
        {lista && lista.length === 0 && !form && (
          <p className="text-sm text-slate-500">Nenhuma assembleia convocada pelo app ainda.</p>
        )}
      </div>
    </section>
  );
}

function FormAssembleia({ form: f, setForm, modelos, antecedencia, nomeAdm, editando, erro, salvando, onSalvar, onFechar }: {
  form: Form; setForm: (f: Form) => void; modelos: ModeloPauta[]; antecedencia: number; nomeAdm: string | null;
  editando: boolean; erro: string | null; salvando: boolean; onSalvar: () => void; onFechar: () => void;
}) {
  const inp = "w-full text-sm rounded-md border border-slate-300 px-2.5 py-1.5";
  const set = (p: Partial<Form>) => setForm({ ...f, ...p });
  const dias = diasAte(f.data);
  const itens = f.pauta.filter((p) => p.trim());
  const pronto = !!f.data && !!f.hora1 && !!f.hora2 && f.hora2 > f.hora1 && itens.length > 0
    && (f.formato === "VIRTUAL" || !!f.local.trim()) && (f.formato === "PRESENCIAL" || !!f.link.trim())
    && (dias ?? -1) >= 0;
  const mover = (i: number, d: number) => {
    const ps = [...f.pauta]; const j = i + d;
    if (j < 0 || j >= ps.length) return;
    [ps[i], ps[j]] = [ps[j], ps[i]]; set({ pauta: ps });
  };
  const adicionarModelo = (texto: string) => {
    const ps = f.pauta.filter((p) => p.trim());
    // "Assuntos gerais" fica sempre por último.
    const ag = ps.findIndex((p) => p.toLowerCase().startsWith("assuntos gerais"));
    if (ag >= 0 && !texto.toLowerCase().startsWith("assuntos gerais")) ps.splice(ag, 0, texto);
    else ps.push(texto);
    set({ pauta: ps });
  };

  return (
    <div className="rounded-lg border border-teal-200 bg-teal-50/30 p-3 mb-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800 text-sm">{editando ? "Editar convocação (rascunho)" : "Nova convocação"}</h3>
        <button onClick={onFechar} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-500">Tipo</label>
          <div className="flex gap-1.5 mt-1">
            {[["AGO", "Ordinária (AGO)"], ["AGE", "Extraordinária (AGE)"]].map(([v, r]) => (
              <button key={v} type="button" onClick={() => set({ tipo: v })}
                className={`text-xs px-2.5 py-1.5 rounded-md border ${f.tipo === v ? "bg-brand-700 text-white border-brand-700" : "bg-white text-slate-700 border-slate-300"}`}>
                {r}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-1">
            {f.tipo === "AGO" ? "Anual: contas, orçamento e eleição." : "Qualquer outro assunto (obra, chamada extra, regimento…)."}
          </p>
        </div>
        <div>
          <label className="text-xs text-slate-500">Formato</label>
          <div className="flex gap-1.5 mt-1">
            {FORMATOS.map((x) => (
              <button key={x.v} type="button" onClick={() => set({ formato: x.v })}
                className={`text-xs px-2.5 py-1.5 rounded-md border ${f.formato === x.v ? "bg-brand-700 text-white border-brand-700" : "bg-white text-slate-700 border-slate-300"}`}>
                {x.rotulo}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-xs text-slate-500">Data</label>
          <input type="date" className={inp} value={f.data} onChange={(e) => set({ data: e.target.value })} />
        </div>
        <div>
          <label className="text-xs text-slate-500">1ª convocação</label>
          <input type="time" className={inp} value={f.hora1}
            onChange={(e) => set({ hora1: e.target.value, hora2: somarMinutos(e.target.value, 30) })} />
        </div>
        <div>
          <label className="text-xs text-slate-500">2ª convocação</label>
          <input type="time" className={inp} value={f.hora2} onChange={(e) => set({ hora2: e.target.value })} />
        </div>
      </div>
      {dias !== null && dias < 0 && <p className="text-xs text-rose-600">Esta data já passou.</p>}
      {dias !== null && dias >= 0 && dias < antecedencia && (
        <p className="text-xs text-amber-700 flex items-start gap-1">
          <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
          Faltam só {dias} dia(s). O usual nas convenções é avisar com pelo menos {antecedencia} dias —
          confira o prazo da Convenção do prédio para a assembleia não ser contestada.
        </p>
      )}
      {f.hora2 <= f.hora1 && <p className="text-xs text-rose-600">A 2ª convocação tem de ser depois da 1ª (o usual é 30 minutos).</p>}

      {f.formato !== "VIRTUAL" && (
        <div>
          <label className="text-xs text-slate-500">Local</label>
          <input className={inp} value={f.local} placeholder="Ex.: Salão de festas do condomínio"
            onChange={(e) => set({ local: e.target.value })} />
        </div>
      )}
      {f.formato !== "PRESENCIAL" && (
        <div>
          <label className="text-xs text-slate-500">Link da reunião on-line</label>
          <input className={inp} value={f.link} placeholder="Ex.: https://meet.google.com/..."
            onChange={(e) => set({ link: e.target.value })} />
          <p className="text-[11px] text-slate-400 mt-0.5">A lei exige que o edital diga como acessar, falar e votar — o app escreve isso por você.</p>
        </div>
      )}

      <div>
        <label className="text-xs text-slate-500">Ordem do dia (pauta)</label>
        <div className="flex flex-wrap gap-1 my-1.5">
          {modelos.map((m) => (
            <button key={m.titulo} type="button" onClick={() => adicionarModelo(m.texto)}
              className="text-[11px] px-2 py-0.5 rounded-full border border-slate-300 bg-white text-slate-600 hover:border-teal-400 hover:text-teal-700">
              + {m.titulo}
            </button>
          ))}
        </div>
        <div className="space-y-1.5">
          {f.pauta.map((p, i) => (
            <div key={i} className="flex items-start gap-1.5">
              <span className="text-xs text-slate-500 mt-2 w-4 text-right">{i + 1}.</span>
              <textarea rows={2} className={inp} value={p} placeholder="Descreva o assunto a ser decidido"
                onChange={(e) => { const ps = [...f.pauta]; ps[i] = e.target.value; set({ pauta: ps }); }} />
              <div className="flex flex-col">
                <button type="button" onClick={() => mover(i, -1)} className="text-slate-400 hover:text-slate-700" title="Subir"><ArrowUp size={14} /></button>
                <button type="button" onClick={() => mover(i, 1)} className="text-slate-400 hover:text-slate-700" title="Descer"><ArrowDown size={14} /></button>
                <button type="button" onClick={() => set({ pauta: f.pauta.length > 1 ? f.pauta.filter((_, j) => j !== i) : [""] })}
                  className="text-slate-400 hover:text-rose-600" title="Remover"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => set({ pauta: [...f.pauta, ""] })}
          className="text-xs text-brand-700 inline-flex items-center gap-1 mt-1"><Plus size={13} /> adicionar item</button>
      </div>

      <div>
        <label className="text-xs text-slate-500">Observações (opcional)</label>
        <textarea rows={2} className={inp} value={f.observacoes} placeholder="Ex.: Haverá café. Traga um documento com foto."
          onChange={(e) => set({ observacoes: e.target.value })} />
      </div>

      <div className="rounded-md border border-slate-200 bg-white p-2.5 space-y-1.5">
        <div className="text-xs font-medium text-slate-600 flex items-center gap-1.5">
          <Briefcase size={13} /> Administradora{nomeAdm ? ` (${nomeAdm})` : ""}
        </div>
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input type="checkbox" className="mt-1" checked={f.adm_participa} onChange={(e) => set({ adm_participa: e.target.checked })} />
          <span>A administradora participa da assembleia e faz a ata
            <span className="block text-[11px] text-slate-400">Sai escrito no edital.</span></span>
        </label>
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input type="checkbox" className="mt-1" checked={f.adm_envia} onChange={(e) => set({ adm_envia: e.target.checked })} />
          <span>A administradora envia o e-mail de convocação aos moradores
            <span className="block text-[11px] text-slate-400">O app não manda e-mail: só publica no Portal e avisa no celular e no WhatsApp.</span></span>
        </label>
      </div>

      {erro && <p className="text-sm text-rose-600">{erro}</p>}
      <button disabled={!pronto || salvando} onClick={onSalvar}
        className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-md bg-brand-700 text-white disabled:opacity-50">
        {salvando ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Salvar rascunho
      </button>
      <p className="text-[11px] text-slate-400">Nada é enviado aos moradores agora. Depois de salvar, confira o edital e toque em "Enviar convocação".</p>
    </div>
  );
}

function BotaoPdf({ href, icone: Icone, children }: { href: string; icone: typeof FileText; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer"
      className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50">
      <Icone size={13} /> {children}
    </a>
  );
}

function CartaoAssembleia({ a, antecedencia, onEditar, onMudou, onVotacaoCriada }: {
  a: Assembleia; antecedencia: number; onEditar: () => void; onMudou: () => void; onVotacaoCriada: () => void;
}) {
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [paraAdm, setParaAdm] = useState(false);
  const pelaAdm = a.envio_moradores === "ADMINISTRADORA";
  const acao = useMutation({
    mutationFn: ({ tipo }: { tipo: string }) => {
      if (tipo === "excluir") return api.delete(`/assembleias/${a.id}`);
      if (tipo === "enquete") return api.post(`/assembleias/${a.id}/criar-votacao?modalidade=ENQUETE`);
      if (tipo === "votacao") return api.post(`/assembleias/${a.id}/criar-votacao?modalidade=VOTACAO`);
      return api.post(`/assembleias/${a.id}/${tipo}`);
    },
    onSuccess: (_r, { tipo }) => {
      setErro(null);
      if (tipo === "enviar") {
        setAviso(pelaAdm
          ? "Convocação publicada no Portal. Agora mande o PDF à administradora (abaixo) para ela enviar o e-mail aos moradores."
          : "Convocação enviada. Os e-mails e notificações saem nos próximos minutos.");
        if (pelaAdm) setParaAdm(true);
      }
      if (tipo === "enquete" || tipo === "votacao") {
        setAviso(`${tipo === "enquete" ? "Enquete" : "Votação"} criada como rascunho na lista abaixo — confira o prazo e publique.`);
        onVotacaoCriada();
      }
      onMudou();
    },
    onError: (e) => setErro(msg(e)),
  });
  const st = STATUS[a.status] ?? STATUS.RASCUNHO;
  const ocupado = acao.isPending;

  function enviar() {
    const pouco = a.dias_antecedencia < antecedencia
      ? `\n\nAtenção: faltam só ${a.dias_antecedencia} dia(s). Confira o prazo da Convenção.` : "";
    const canais = pelaAdm
      ? "O edital fica fixado no Quadro de Avisos e sai aviso no celular e no WhatsApp. O E-MAIL aos moradores fica com a administradora."
      : "O edital fica fixado no Quadro de Avisos e sai por e-mail, celular e WhatsApp.";
    if (confirm(`Enviar a convocação a TODOS os moradores agora?\n\n${canais} Depois de enviada, não dá para alterar (só cancelar e convocar de novo).${pouco}`))
      acao.mutate({ tipo: "enviar" });
  }

  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-slate-900">
            <span className={`mr-1.5 align-middle text-[10px] font-bold border rounded px-1.5 py-0.5 ${st.cls}`}>{st.rotulo}</span>
            {a.tipo} · {quandoBr(a.primeira_em)}
          </div>
          <div className="text-xs text-slate-500">
            2ª convocação {hora(a.segunda_em)} · {a.formato === "VIRTUAL" ? "on-line" : a.local}
            {a.formato === "HIBRIDA" && " + on-line"} · {a.pauta.length} item(ns) na pauta
          </div>
        </div>
        {a.status === "RASCUNHO" && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={onEditar} className="text-slate-400 hover:text-slate-700" title="Editar"><Pencil size={15} /></button>
            <button onClick={() => { if (confirm("Apagar este rascunho?")) acao.mutate({ tipo: "excluir" }); }}
              className="text-slate-400 hover:text-rose-600" title="Apagar"><Trash2 size={15} /></button>
          </div>
        )}
      </div>

      <ol className="mt-1.5 text-xs text-slate-600 list-decimal pl-5 space-y-0.5">
        {a.pauta.map((p, i) => <li key={i}>{p}</li>)}
      </ol>

      {a.envios && a.status !== "RASCUNHO" && (
        <div className="mt-2 text-xs text-slate-600 rounded-md bg-slate-50 border border-slate-200 px-2 py-1.5">
          Enviada {a.enviada_em ? quandoBr(a.enviada_em) : ""} por {a.enviada_por} · {a.envios.unidades} unidades no Quadro de
          Avisos · {pelaAdm ? "e-mail a cargo da administradora" : `${a.envios.email_ok} e-mail(s)`} · {a.envios.celular} celular(es)
          {a.envios.email_falhou > 0 && <span className="text-rose-600"> · {a.envios.email_falhou} e-mail(s) falharam</span>}
          {a.lembrete_em && <> · lembrete da véspera enviado</>}
          {a.envios.sem_email.length > 0 && (
            <div className="text-amber-700 mt-0.5">
              Sem e-mail ({a.envios.sem_email.join(", ")}): entregue o edital em mãos e peça assinatura de recebimento.
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 mt-2">
        <BotaoPdf href={assembleiaPdfUrl(a.id, "edital")} icone={FileText}>
          {a.status === "RASCUNHO" ? "Ver edital (minuta)" : "Edital"}
        </BotaoPdf>
        <BotaoPdf href={assembleiaPdfUrl(a.id, "lista-presenca")} icone={Users}>Lista de presença</BotaoPdf>
        <BotaoPdf href={assembleiaPdfUrl(a.id, "procuracao")} icone={FileSignature}>Procuração</BotaoPdf>
        {a.status !== "CANCELADA" && (
          <button onClick={() => setParaAdm(!paraAdm)}
            className={`inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border ${paraAdm ? "border-teal-400 bg-teal-50 text-teal-800" : "border-teal-300 text-teal-700"}`}>
            <Briefcase size={13} /> Para a administradora
          </button>
        )}
        {a.status !== "RASCUNHO" && (
          <BotaoPdf href={assembleiaPdfUrl(a.id, "comprovante")} icone={ClipboardList}>Comprovante de envio</BotaoPdf>
        )}
        {a.status === "RASCUNHO" && (
          <button onClick={enviar} disabled={ocupado}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-brand-700 text-white disabled:opacity-50">
            {ocupado ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Enviar convocação
          </button>
        )}
        {a.status !== "CANCELADA" && !a.enquete_id && a.status !== "REALIZADA" && (
          <button onClick={() => acao.mutate({ tipo: "enquete" })} disabled={ocupado}
            title="Consulta antes da assembleia: responde quem mora, 1 por apto, sem ata"
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-violet-300 text-violet-700 disabled:opacity-50">
            <BarChart3 size={13} /> Criar enquete prévia
          </button>
        )}
        {a.status !== "CANCELADA" && !a.votacao_id && (
          <button onClick={() => acao.mutate({ tipo: "votacao" })} disabled={ocupado}
            title="Votação on-line com os itens da pauta (vota o proprietário ou procurador), com ata"
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-slate-300 text-slate-700 disabled:opacity-50">
            <Vote size={13} /> Criar votação on-line
          </button>
        )}
        {a.status === "ENVIADA" && (
          <>
            <button onClick={() => acao.mutate({ tipo: "realizada" })} disabled={ocupado}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-slate-300 text-slate-700 disabled:opacity-50">
              <CheckCircle2 size={13} /> Marcar como realizada
            </button>
            <button onClick={() => { if (confirm("Cancelar esta assembleia? Os moradores serão avisados.")) acao.mutate({ tipo: "cancelar" }); }}
              disabled={ocupado}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-rose-300 text-rose-700 disabled:opacity-50">
              <Ban size={13} /> Cancelar
            </button>
          </>
        )}
      </div>
      {paraAdm && <ParaAdministradora a={a} />}
      {aviso && <p className="text-xs text-teal-700 mt-1.5">{aviso}</p>}
      {erro && <p className="text-xs text-rose-600 mt-1.5">{erro}</p>}
    </div>
  );
}

/** Baixar o PDF único + texto pronto do e-mail (o síndico anexa e envia do e-mail dele). */
function ParaAdministradora({ a }: { a: Assembleia }) {
  const { data, isLoading } = useQuery<{ assunto: string; corpo: string; administradora: string | null }>({
    queryKey: ["assembleia-texto-adm", a.id, a.status, a.administradora_participa, a.envio_moradores],
    queryFn: () => api.get(`/assembleias/${a.id}/texto-administradora`),
  });
  return (
    <div className="mt-2 rounded-md border border-teal-200 bg-teal-50/40 p-2.5 text-xs text-slate-700 space-y-2">
      <p>
        Envie a convocação para a administradora{data?.administradora ? ` (${data.administradora})` : ""} pelo seu e-mail.
        {a.status === "RASCUNHO" && <span className="text-amber-700"> O edital ainda sai como MINUTA: envie a convocação antes, se esta for a versão final.</span>}
      </p>
      <ol className="list-decimal pl-5 space-y-1.5">
        <li>
          <a href={assembleiaPdfUrl(a.id, "pacote-administradora")} download
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-brand-700 text-white">
            <Download size={13} /> Baixar o PDF (edital, lista de presença, procuração{a.status !== "RASCUNHO" ? " e comprovante" : ""})
          </a>
        </li>
        <li>
          {isLoading ? <Loader2 size={13} className="animate-spin inline" /> : <BotoesEmail assunto={data?.assunto} corpo={data?.corpo} pdfUrl={assembleiaPdfUrl(a.id, "pacote-administradora")} />}
        </li>
        <li>Anexe o PDF baixado (clipe do e-mail) e envie.</li>
      </ol>
      <p className="text-[11px] text-amber-700">{AVISO_ANEXO}</p>
      {data && (
        <pre className="whitespace-pre-wrap font-sans text-[11px] text-slate-600 bg-white border border-slate-200 rounded p-2 max-h-48 overflow-auto">
          {`${data.assunto}\n\n${data.corpo}`}
        </pre>
      )}
    </div>
  );
}

// =====================================================================
// Morador
// =====================================================================
export function AssembleiasMorador({ condominioId }: { condominioId: number }) {
  const { data: lista } = useQuery<Assembleia[]>({
    queryKey: ["assembleias", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/assembleias`),
  });
  const visiveis = (lista ?? []).filter((a) => a.status === "ENVIADA" || a.status === "CANCELADA"
    || (a.status === "REALIZADA" && a.dias_antecedencia > -60));
  if (!visiveis.length) return null;
  return (
    <section className="mb-5">
      <h2 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
        <Gavel size={15} className="text-teal-600" /> Assembleias
      </h2>
      <div className="space-y-2">
        {visiveis.map((a) => (
          <div key={a.id} className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="font-medium text-slate-900 text-sm">
              {a.status !== "ENVIADA" && (
                <span className={`mr-1.5 align-middle text-[10px] font-bold border rounded px-1.5 py-0.5 ${STATUS[a.status].cls}`}>
                  {STATUS[a.status].rotulo}
                </span>
              )}
              {a.nome_tipo}
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              {quandoBr(a.primeira_em)} (1ª convocação) · 2ª às {hora(a.segunda_em)}
            </div>
            <div className="text-xs text-slate-600">
              {a.formato !== "VIRTUAL" && <>Local: {a.local}</>}
              {a.formato !== "PRESENCIAL" && a.link && a.status === "ENVIADA" && (
                <>{a.formato === "HIBRIDA" && " · "}<a href={a.link} target="_blank" rel="noreferrer"
                  className="text-brand-700 underline">entrar na reunião on-line</a></>
              )}
            </div>
            <ol className="mt-1.5 text-xs text-slate-600 list-decimal pl-5 space-y-0.5">
              {a.pauta.map((p, i) => <li key={i}>{p}</li>)}
            </ol>
            {a.status === "ENVIADA" && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                <BotaoPdf href={assembleiaPdfUrl(a.id, "edital")} icone={FileText}>Edital</BotaoPdf>
                <BotaoPdf href={assembleiaPdfUrl(a.id, "procuracao")} icone={FileSignature}>Modelo de procuração</BotaoPdf>
              </div>
            )}
            {a.status === "ENVIADA" && (
              <p className="text-[11px] text-slate-400 mt-1.5">
                Não pode ir? Preencha a procuração, assine à mão ou pelo gov.br (gratuito) e entregue ao seu representante.
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
